import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Navigate, useNavigate } from "react-router-dom";
import PatientStatusChecker from "../pages/opt-in-opt-out/components/PatientStatusChecker";
import { useAppSelector } from "../store/hooks";
import { ThemeProvider } from "@mui/material";
import { useTokenExpiry } from "../auth/patient-auth/useTokenExpiry";
import AlertPopUp from "../components/alert-pop-up/AlertPopUp";
import { patientTheme } from "../styles/theme";
import { SnackbarProvider } from "notistack";
import PatientThemeResponsiveSnackbar from "../components/v2/custom-snackbar/PatientThemeResponsiveSnackbar";
import ConfirmationModal from "../components/confirmation-modal/ConfirmationModal";
import { useTranslation } from "react-i18next";
import styles from "../components/v2/custom-snackbar/PatientThemeResponsiveSnackbar.module.scss";
import AppInsightsUserContext from "../app-insights/AppInsightsUserContext";

interface NavigationBlockContextType {
  hasChanges: boolean;
  setHasChanges: (val: boolean) => void;
  requestNavigation: (callback: () => void) => void;
  confirmLeave: () => void;
  cancelLeave: () => void;
  setSavedChanges: (val: boolean) => void;
}

const NavigationBlockContext = createContext<NavigationBlockContextType | null>(
  null
);
export const useNavigationBlock = () => {
  const context = useContext(NavigationBlockContext);
  if (!context) {
    throw new Error(
      "useNavigationBlock must be used within PatientProtectedRoute"
    );
  }
  return context;
};

interface PatientProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
}

const PatientProtectedRoute: React.FC<PatientProtectedRouteProps> = ({
  children,
  redirectTo = "/",
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const tokenResponses = useAppSelector((state) => state.patientAuth);
  const { isAlertOpen, closeAlert, remainingMinutes } = useTokenExpiry();

  // Navigation-block state
  const [hasChanges, setHasChanges] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<() => void>();

  const requestNavigation = useCallback(
    (callback: () => void) => {
      if (hasChanges) {
        setPendingNavigation(() => callback);
        setShowModal(true);
      } else {
        callback();
      }
    },
    [hasChanges]
  );

  const [isSaved, setIsSaved] = useState(false); // New state for saved status

const setSavedChanges = (val: boolean) => {
  setIsSaved(val);
};

  const confirmLeave = useCallback(() => {
    if (pendingNavigation) pendingNavigation();
    setHasChanges(false);
    setPendingNavigation(undefined);
    setShowModal(false);
  }, [pendingNavigation]);

  const cancelLeave = () => {
    setPendingNavigation(undefined);
    setShowModal(false);
  };

  // Handle browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasChanges]);

  // Handle browser back/forward navigation
  const preventBackNavigation = useCallback(() => {
    if (hasChanges) {
      setPendingNavigation(() => () => {
        window.removeEventListener("popstate", preventBackNavigation);
        navigate(-2);
      });
      setShowModal(true);
      window.history.pushState(null, "", window.location.href);
    }
  }, [hasChanges, navigate]);
  

  useEffect(() => {
    // Remove dummy state if saved and no changes remain
    if (isSaved && !hasChanges) {
      // Ensure we clean up popstate listener if still present
      window.removeEventListener("popstate", preventBackNavigation);
  
      // Go back one step to remove the dummy history state
      if (window.history.state === null) {
        window.history.go(-1);
      }
  
      // Reset saved flag
      setIsSaved(false);
    }
  }, [isSaved, hasChanges]);
  
  useEffect(() => {
    if (hasChanges) {
      window.history.pushState(null, "", window.location.href);
      window.addEventListener("popstate", preventBackNavigation);
    }
  
    return () => {
      window.removeEventListener("popstate", preventBackNavigation);
    };
  }, [hasChanges, preventBackNavigation]);

  if (!tokenResponses || !tokenResponses.accessToken) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <NavigationBlockContext.Provider
      value={{
        hasChanges,
        setHasChanges,
        requestNavigation,
        confirmLeave,
        cancelLeave,
        setSavedChanges
      }}
    >
      <ThemeProvider theme={patientTheme}>
      <AppInsightsUserContext />
        <SnackbarProvider
          anchorOrigin={{ horizontal: "center", vertical: "top" }}
          Components={{
            success: PatientThemeResponsiveSnackbar,
            error: PatientThemeResponsiveSnackbar,
          }}
          autoHideDuration={4000}
          maxSnack={1}
          classes={{
            containerAnchorOriginTopCenter: styles.snackbarContainer,
          }}
        >
          <PatientStatusChecker>{children}</PatientStatusChecker>

          {isAlertOpen && remainingMinutes! > 0 && (
            <AlertPopUp
              open={isAlertOpen}
              onClose={closeAlert}
              remainingMinutes={remainingMinutes!}
            />
          )}

          <ConfirmationModal
            open={showModal}
            onCancel={cancelLeave}
            onConfirm={confirmLeave}
            title={t("confirmationModal.title")}
            description={t("confirmationModal.description")}
            cancelText={t("cancel")}
            confirmText={t("confirmationModal.leave")}
          />
        </SnackbarProvider>
      </ThemeProvider>
    </NavigationBlockContext.Provider>
  );
};

export default PatientProtectedRoute;
